// Chat module v2: direct messages and groups (organization-wide, WhatsApp/Teams-style),
// plus topic channels tied to a workspace. Messages are polled by the client (see docs/07:
// no realtime transport is set up yet — this is the honest MVP, not a fake websocket layer).
import { z } from 'zod';

const Uuid = z.string().uuid();

export const NICKNAME_RE = /^[a-z0-9_.]{2,30}$/;
export const NicknameInput = z.object({
  nickname: z.string().trim().toLowerCase().regex(NICKNAME_RE, 'Usa 2-30 caratteri: lettere minuscole, numeri, punti o underscore').nullable(),
});
export type NicknameInput = z.input<typeof NicknameInput>;

export const ChatChannelInput = z.object({
  workspaceId: Uuid,
  name: z.string().trim().toLowerCase().min(1, 'Scrivi un nome').max(40).regex(/^[a-z0-9][a-z0-9-]*$/, 'Solo lettere minuscole, numeri e trattini'),
  topic: z.string().trim().max(200).nullable().default(null),
  isPrivate: z.boolean().default(false),
});
export type ChatChannelInput = z.input<typeof ChatChannelInput>;

export const GroupInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(60),
  memberIds: z.array(Uuid).min(1, 'Aggiungi almeno una persona').max(100),
});
export type GroupInput = z.input<typeof GroupInput>;

export const DmInput = z.object({ userId: Uuid });

export const MessageInput = z.object({ body: z.string().trim().min(1, 'Scrivi un messaggio').max(4000) });

export const MessageListQuery = z.object({ after: Uuid.optional() });
export type MessageListQuery = z.input<typeof MessageListQuery>;

// ------------------------------------------------------------------ responses
export type ChatChannelKind = 'workspace' | 'dm' | 'group';

export interface PersonResult {
  userId: string;
  displayName: string;
  nickname: string | null;
}

export interface ChatChannel {
  id: string;
  kind: ChatChannelKind;
  workspaceId: string | null;
  workspaceName: string | null;
  /** Display name: the channel/group name, or (for a DM) the other person's name. */
  name: string;
  topic: string | null;
  isPrivate: boolean;
  /** The other member, only set for kind = 'dm'. */
  peer: { id: string; displayName: string; nickname: string | null } | null;
  memberCount: number;
  unreadCount: number;
  lastMessage: { body: string; createdAt: string; mine: boolean } | null;
  lastMessageAt: string | null;
  isMember: boolean;
  canPost: boolean;
  canManage: boolean;
}

export interface ChatMessage {
  id: string;
  channelId: string;
  author: { id: string; displayName: string };
  body: string;
  createdAt: string;
  editedAt: string | null;
  mine: boolean;
  /** Only meaningful for DMs: whether the other person has read up to this message. */
  seen: boolean;
}
